// src/pages/Masters/MProject/MProjectActionBar/ActionBar.jsx

import React, { useState } from "react";
import Add from "./Add";
import DynamicEditComponent from "./Edit";
import Export from "./Export";
import Search from "./Search";
import Delete from "./Delete";
import "./ActionBar.css";

const ActionBar = ({
  title,
  fields,
  apiEndpoint,
  data,
  selectedRow,
  onRowSelect,
  userRole,
  onRefresh,
  onSearch,
  rowKey = "id",
  deleteMessage = "Are you sure you want to delete this item?",
  fetchAllData,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isAdmin = userRole === "admin";

  const handleAdd = () => setShowAddModal(true);
  const handleCloseAdd = () => setShowAddModal(false);

  const handleEdit = () => {
    if (!selectedRow) {
      alert("Please select a row to edit!");
      return;
    }
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    onRowSelect(null);
  };

  const handleDeleteConfirmation = (id) => {
    setSelectedDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedDeleteId(null);
  };

  const renderModal = (Component, props, handleClose) => (
    <div className="modal-overlay">
      <div className="modal-container">
        <Component {...props} onClose={handleClose} />
      </div>
    </div>
  );

  return (
    <div className="action-bar">
      {/* Desktop View */}
      <div className="desktop-actions">
        <div className="button-group">
          {isAdmin && (
            <>
              <button
                onClick={handleAdd}
                className="action-button add-button"
                aria-label="Add new item"
              >
                Add
              </button>
              <button
                onClick={handleEdit}
                className="action-button edit-button"
                aria-label="Edit selected item"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  if (!selectedRow) {
                    alert("Please select a row to delete!");
                    return;
                  }
                  handleDeleteConfirmation(selectedRow[rowKey]);
                }}
                className="action-button delete-button"
                aria-label="Delete selected item"
              >
                Delete
              </button>
            </>
          )}
          <Export
            fetchAllData={fetchAllData}
            className="action-button export-button"
          />
          <Search onSearch={onSearch} className="search-component" />
        </div>
      </div>

      {/* Mobile View */}
      <div className="mobile-actions">
        <div className="mobile-header">
          <h2 className="mobile-title">{title}</h2>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="mobile-menu-button"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? "Close" : "Actions"}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="mobile-menu">
            {isAdmin && (
              <>
                <button
                  onClick={() => {
                    handleAdd();
                    setIsMobileMenuOpen(false);
                  }}
                  className="mobile-action-button add-button"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    handleEdit();
                    setIsMobileMenuOpen(false);
                  }}
                  className="mobile-action-button edit-button"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (!selectedRow) {
                      alert("Please select a row to delete!");
                      return;
                    }
                    handleDeleteConfirmation(selectedRow[rowKey]);
                    setIsMobileMenuOpen(false);
                  }}
                  className="mobile-action-button delete-button"
                >
                  Delete
                </button>
              </>
            )}
            <Export
              fetchAllData={fetchAllData}
              className="mobile-action-button export-button"
            />
            <Search onSearch={onSearch} className="mobile-search" />
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal &&
        renderModal(
          Add,
          {
            title: `Add ${title}`,
            fields,
            apiEndpoint,
            onSuccess: onRefresh,
            onClose: handleCloseAdd,
          },
          handleCloseAdd
        )}

      {showEditModal &&
        selectedRow &&
        renderModal(
          DynamicEditComponent,
          {
            fields,
            data: selectedRow,
            apiEndpoint,
            rowKey,
            onSave: () => {
              onRefresh();
              handleCloseEdit();
              onRowSelect(null);
            },
          },
          handleCloseEdit
        )}

      {showDeleteModal &&
        renderModal(
          Delete,
          {
            itemId: selectedDeleteId,
            apiEndpoint,
            rowKey,
            message: deleteMessage,
            onDelete: () => {
              onRefresh();
              handleCancelDelete();
            },
            onCancel: handleCancelDelete,
          },
          handleCancelDelete
        )}
    </div>
  );
};

export default ActionBar;